import { useState } from 'react';
import { LayoutGrid, History, ShoppingCart, Info, Palette } from 'lucide-react';
import { CartProvider, useCart } from '../context/CartContext';
import { ProductGrid } from '../components/ProductGrid';
import { Cart } from '../components/Cart';
import { OrderHistory } from '../components/OrderHistory';
import { OrderDetails } from '../components/OrderDetails';

type PlvView = 'catalog' | 'cart' | 'orders' | 'order-details';

function NavButton({
  active,
  onClick,
  children,
  fullWidth,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
  fullWidth?: boolean;
}) {
  return (
    <button
      className={`tw-inline-flex tw-items-center tw-justify-center tw-gap-2 tw-px-4 tw-py-2 tw-rounded-lg tw-text-sm tw-font-medium tw-border tw-transition-colors ${
        fullWidth ? 'tw-w-full' : ''
      } ${
        active
          ? 'tw-bg-affinicar-secondary tw-text-white tw-border-affinicar-secondary'
          : 'tw-bg-white tw-text-gray-600 tw-border-gray-300 hover:tw-bg-gray-50'
      }`}
      onClick={onClick}
    >
      {children}
    </button>
  );
}

function PlvContent() {
  const [currentView, setCurrentView] = useState<PlvView>('catalog');
  const [selectedOrderId, setSelectedOrderId] = useState<number | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const { state: cartState } = useCart();

  const handleViewOrder = (orderId: number) => {
    setSelectedOrderId(orderId);
    setCurrentView('order-details');
  };

  const handleOrderCreated = () => {
    setRefreshKey(prev => prev + 1);
    setCurrentView('orders');
  };

  const renderContent = () => {
    switch (currentView) {
      case 'catalog':
        return <ProductGrid key={refreshKey} />;
      case 'cart':
        return (
          <Cart
            onBackToCatalog={() => setCurrentView('catalog')}
            onOrderCreated={handleOrderCreated}
          />
        );
      case 'orders':
        return (
          <OrderHistory
            onViewOrder={handleViewOrder}
            onBackToCatalog={() => setCurrentView('catalog')}
          />
        );
      case 'order-details':
        return selectedOrderId ? (
          <OrderDetails
            orderId={selectedOrderId}
            onBack={() => setCurrentView('orders')}
          />
        ) : (
          <OrderHistory
            onViewOrder={handleViewOrder}
            onBackToCatalog={() => setCurrentView('catalog')}
          />
        );
      default:
        return <ProductGrid key={refreshKey} />;
    }
  };

  return (
    <div className="tw-pt-4">
      {/* Page header */}
      <div className="tw-mb-6">
        <div className="tw-flex tw-items-center tw-gap-3">
          <Palette className="tw-w-8 tw-h-8 tw-text-affinicar-primary" />
          <h1 className="tw-text-2xl tw-font-bold tw-text-gray-900 tw-mb-0">
            PLV Personnalisée
          </h1>
        </div>
      </div>

      {/* Navigation */}
      <div className="tw-mb-4">
        <div className="tw-bg-white tw-rounded-lg tw-border tw-border-gray-200 tw-shadow-sm tw-px-4 tw-py-3">
          {/* Desktop */}
          <div className="tw-hidden md:tw-flex tw-justify-between tw-items-center">
            <div className="tw-flex tw-gap-2">
              <NavButton active={currentView === 'catalog'} onClick={() => setCurrentView('catalog')}>
                <LayoutGrid className="tw-w-4 tw-h-4" />
                Catalogue
              </NavButton>
              <NavButton active={currentView === 'orders'} onClick={() => setCurrentView('orders')}>
                <History className="tw-w-4 tw-h-4" />
                Mes commandes
              </NavButton>
            </div>
            <div className="tw-flex tw-items-center tw-gap-3">
              {cartState.totalItems > 0 && (
                <span className="tw-text-sm tw-text-gray-500 tw-flex tw-items-center tw-gap-1">
                  <Info className="tw-w-4 tw-h-4" />
                  {cartState.totalItems} article{cartState.totalItems > 1 ? 's' : ''} dans le panier
                </span>
              )}
              <div className="tw-relative">
                <NavButton active={currentView === 'cart'} onClick={() => setCurrentView('cart')}>
                  <ShoppingCart className="tw-w-4 tw-h-4" />
                  Panier
                </NavButton>
                {cartState.totalItems > 0 && (
                  <span className="tw-absolute -tw-top-2 -tw-right-2 tw-bg-red-500 tw-text-white tw-text-xs tw-font-bold tw-rounded-full tw-w-5 tw-h-5 tw-flex tw-items-center tw-justify-center">
                    {cartState.totalItems}
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Mobile */}
          <div className="md:tw-hidden tw-space-y-2">
            <div className="tw-grid tw-grid-cols-2 tw-gap-2">
              <NavButton active={currentView === 'catalog'} onClick={() => setCurrentView('catalog')} fullWidth>
                <LayoutGrid className="tw-w-4 tw-h-4" />
                Catalogue
              </NavButton>
              <NavButton active={currentView === 'orders'} onClick={() => setCurrentView('orders')} fullWidth>
                <History className="tw-w-4 tw-h-4" />
                Commandes
              </NavButton>
            </div>
            <div className="tw-flex tw-justify-end tw-items-center tw-gap-3">
              {cartState.totalItems > 0 && (
                <span className="tw-text-xs tw-text-gray-500">
                  {cartState.totalItems} article{cartState.totalItems > 1 ? 's' : ''}
                </span>
              )}
              <div className="tw-relative">
                <NavButton active={currentView === 'cart'} onClick={() => setCurrentView('cart')}>
                  <ShoppingCart className="tw-w-4 tw-h-4" />
                  Panier
                </NavButton>
                {cartState.totalItems > 0 && (
                  <span className="tw-absolute -tw-top-2 -tw-right-2 tw-bg-red-500 tw-text-white tw-text-xs tw-font-bold tw-rounded-full tw-w-5 tw-h-5 tw-flex tw-items-center tw-justify-center">
                    {cartState.totalItems}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {renderContent()}
    </div>
  );
}

export function PlvPage() {
  return (
    <CartProvider>
      <PlvContent />
    </CartProvider>
  );
}
